import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2, ArrowRight, Heart } from "lucide-react";
import { BACKEND_URL } from "@/lib/rn3";

// Public submit goes through axios (no auth header) instead of the authenticated `api` instance
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const Refer = () => {
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const referrerId = params.get("ref") || "";
  const isFromQR = (params.get("src") || "").toLowerCase() === "qr_code";

  const [lang, setLang] = useState(navigator.language?.toLowerCase().startsWith("es") ? "es" : "en");
  const [referrer, setReferrer] = useState(null);
  const [referrerError, setReferrerError] = useState(false);
  const [form, setForm] = useState({ referred_name: "", referred_email: "", referred_phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const t = (en, es) => (lang === "es" ? es : en);

  useEffect(() => {
    if (!referrerId) return;
    (async () => {
      try {
        const { data } = await publicApi.get(`/referrals/referrer/${referrerId}`);
        setReferrer(data);
      } catch {
        setReferrerError(true);
      }
    })();
  }, [referrerId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.referred_name.trim()) {
      toast.error(t("Please enter your friend's name", "Por favor ingresa el nombre de tu amigo"));
      return;
    }
    setBusy(true);
    try {
      await publicApi.post("/referrals/submit", {
        referrer_client_id: referrerId || null,
        referred_name: form.referred_name.trim(),
        referred_email: form.referred_email.trim() || null,
        referred_phone: form.referred_phone.trim() || null,
        notes: form.notes.trim() || null,
        source: isFromQR ? "qr" : "web",
      });
      setDone(true);
    } catch {
      toast.error(t("Submission failed. Please try again.", "Envío fallido. Por favor intenta de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="public-refer-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="font-serif-display text-xl">Roman Numerals III</Link>
          <div className="inline-flex border border-[#E5E5E5] bg-white" data-testid="refer-lang-toggle">
            {["en", "es"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 text-xs uppercase tracking-widest transition ${lang === l ? "bg-[#0A0A0A] text-white" : "text-[#737373] hover:bg-[#FAFAFA]"}`}
                data-testid={`refer-lang-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {done ? (
          <div className="border border-[#C4A252] bg-white p-10 text-center" data-testid="refer-success">
            <CheckCircle2 className="w-14 h-14 text-[#15803D] mx-auto" />
            <h1 className="font-serif-display text-4xl mt-6">{t("Thank you!", "¡Gracias!")}</h1>
            <div className="border-t border-[#C4A252] w-12 mx-auto mt-4 mb-5" />
            <p className="text-[#404040] leading-relaxed max-w-md mx-auto">
              {t(
                `We'll reach out to ${form.referred_name.split(" ")[0]} within 1 business day. Thanks for sharing — referrals are the kindest compliment.`,
                `Contactaremos a ${form.referred_name.split(" ")[0]} en 1 día hábil. Gracias por compartir — las referencias son el mejor cumplido.`
              )}
            </p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-[#C4A252] text-xs uppercase tracking-widest">
              <Heart className="w-3.5 h-3.5" /> — Adelis
            </div>
            <Link to="/" className="rn3-btn-primary mt-8" data-testid="refer-back-home">
              {t("Back to Home", "Volver al inicio")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E5E5] p-8 md:p-10">
            <div className="rn3-eyebrow inline-flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-[#C4A252]" /> {t("Refer a Friend", "Refiere un amigo")}</div>
            <h1 className="font-serif-display text-3xl md:text-4xl mt-2" data-testid="refer-headline">
              {referrer
                ? t(`${referrer.first_name}, who else needs us?`, `${referrer.first_name}, ¿quién más nos necesita?`)
                : t("Refer a friend to Roman Numerals III", "Refiere un amigo a Roman Numerals III")}
            </h1>
            <div className="border-t border-[#C4A252] w-12 mt-4 mb-5" />
            <p className="text-[#404040] leading-relaxed">
              {referrer
                ? t(
                    `Send us someone who could use bookkeeping, tax prep, ITIN help, notary, or translation. We'll take great care of them — and we'll thank you when they sign on.`,
                    `Envíanos a alguien que necesite contabilidad, impuestos, ITIN, notaría o traducción. Los cuidaremos bien — y te lo agradeceremos cuando se unan.`
                  )
                : t(
                    `Tell us about someone who could use our bilingual financial services. Bookkeeping, taxes, ITIN, notary, translation — we handle it all.`,
                    `Cuéntanos sobre alguien que necesite nuestros servicios financieros bilingües. Contabilidad, impuestos, ITIN, notaría, traducción — lo manejamos todo.`
                  )}
            </p>
            {referrerError && !referrer && (
              <div className="mt-3 text-xs text-[#8B1C22]" data-testid="refer-bad-ref">
                {t("(That referral link looked off, but no worries — submit below anyway.)", "(Ese enlace parecía incorrecto, pero no te preocupes — envía el formulario.)")}
              </div>
            )}

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="rn3-eyebrow block mb-2">{t("Your friend's name", "Nombre de tu amigo")} *</label>
                <input
                  value={form.referred_name}
                  onChange={(e) => setForm({ ...form, referred_name: e.target.value })}
                  required
                  className="rn3-input w-full"
                  placeholder={t("Full name", "Nombre completo")}
                  data-testid="refer-referred-name"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="rn3-eyebrow block mb-2">{t("Their email", "Correo electrónico")}</label>
                  <input
                    type="email"
                    value={form.referred_email}
                    onChange={(e) => setForm({ ...form, referred_email: e.target.value })}
                    className="rn3-input w-full"
                    placeholder="friend@example.com"
                    data-testid="refer-referred-email"
                  />
                </div>
                <div>
                  <label className="rn3-eyebrow block mb-2">{t("Their phone", "Teléfono")}</label>
                  <input
                    type="tel"
                    value={form.referred_phone}
                    onChange={(e) => setForm({ ...form, referred_phone: e.target.value })}
                    className="rn3-input w-full"
                    placeholder="(555) 123-4567"
                    data-testid="refer-referred-phone"
                  />
                </div>
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{t("What do they need? (optional)", "¿Qué necesitan? (opcional)")}</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rn3-input w-full"
                  placeholder={t("Tax help, bookkeeping, ITIN, notary, translation, anything else…", "Impuestos, contabilidad, ITIN, notaría, traducción, lo que sea…")}
                  data-testid="refer-notes"
                />
              </div>

              <p className="text-xs text-[#737373]">
                {t(
                  "We only contact your friend once. They can opt out at any time. We never share their info.",
                  "Solo los contactamos una vez. Pueden cancelar en cualquier momento. Nunca compartimos su información."
                )}
              </p>

              <button type="submit" disabled={busy} className="rn3-btn-primary w-full justify-center" data-testid="refer-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {t("Send Referral", "Enviar referencia")}
              </button>
            </form>
          </div>
        )}

        <div className="mt-8 text-xs text-[#737373] text-center">
          {t("Need our services yourself?", "¿Necesitas nuestros servicios?")}{" "}
          <Link to="/intake" className="underline hover:text-[#8B1C22]">{t("Start an intake", "Iniciar admisión")}</Link>
        </div>
      </div>
    </div>
  );
};

export default Refer;
